import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown, faMinus } from '@fortawesome/free-solid-svg-icons';
import { useGraphData } from '../../hooks/useMeters';
import { buildWidgetConfig } from '../../services/meterApi';
import { financialYearWindow, comparePeriods } from '../../utils/financialYearWindow';
import { stpTreatedComparison } from '../../utils/waterDerivations';
import { metersBehind } from '../../utils/meterContributors';
import ContributorsPopover from './ContributorsPopover';

/**
 * One figure for the latest day it reported, against the period before it.
 *
 * The page held two of these — a general card and a copy whose only difference
 * was computing its value from two KPIs. That arithmetic returns the same
 * shape as the general comparison now, so there is one card.
 *
 * A card whose figure is derived asks for its KPIs to be kept apart. It
 * subtracts one from another, and without that the response arrives summed by
 * day with no KPI on the rows to subtract by — so both terms read as zero, the
 * difference is zero every day, and the card reports that nothing was treated
 * on a plant treating ninety cubic metres a day.
 */

/**
 * Reads the rows out of the widget response, whichever shape it arrives in.
 * @param {object} payload - Response from the graph endpoint.
 * @returns {Array<object>} The rows, empty when there are none.
 */
const rowsOf = (payload) => {
  const rows = payload?.data?.data ?? payload?.data ?? [];
  return Array.isArray(rows) ? rows : [];
};

/**
 * The arrow that suits a direction of travel.
 * @param {number} change - Percentage change.
 * @returns {object} A FontAwesome icon.
 */
const arrowFor = (change) => {
  if (change > 0) return faArrowUp;
  if (change < 0) return faArrowDown;
  return faMinus;
};

/**
 * The class that tones a change.
 * @param {number} change - Percentage change.
 * @returns {string} A modifier for `.kpi-change`.
 */
const toneFor = (change) => {
  if (change > 0) return 'positive';
  if (change < 0) return 'negative';
  return 'neutral';
};

/**
 * What the card is, in words the reader does not need the plant to explain.
 *
 * The titles name points in the plant — ETP inlet, STP outlet, "From Borewell
 * to Admin" — which is exactly right for the people who run it and tells
 * nobody else which water is being counted. This sits at the foot of every
 * card, so the line is in the same place on each one rather than shifting with
 * the length of the figure above it.
 *
 * @param {string} description - The metric's own wording.
 * @returns {object|null} The line, or nothing when a metric has none.
 */
const CardAbout = ({ description }) =>
  description ? <p className="ws-about">{description}</p> : null;

/**
 * @param {object} metric - One entry from WATER_METRICS.
 * @param {string} currentFY - Financial year the figure is scoped to.
 * @param {string|number} locationId - Location filter from the page header.
 * @param {string|number} subLocationId - Sub-location filter from the page header.
 * @param {Array<object>} meters - Every meter the page holds, so the card can
 *   name the ones its figure is calculated from without another request.
 */
const WaterSummaryCard = ({ metric, currentFY, locationId, subLocationId, meters }) => {
  const config = useMemo(() => {
    const window = financialYearWindow(currentFY);
    if (!window) return null;

    const built = {
      financialYear: currentFY,
      ...window,
      locationId: locationId?.toString() || '1',
      title: metric.title,
      module: 'Water',
      category: metric.category,
      subCategory: metric.subCategory,
      kpiType: 'EXTERNAL',
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: 'days',
      xAxisField: 'displayPeriods',
    };

    if (subLocationId) built.subLocationId = subLocationId.toString();
    if (metric.kpi) built.kpi = metric.kpi;
    if (metric.derived) built.stackByField = 'kpi';

    return buildWidgetConfig(built);
  }, [metric, currentFY, locationId, subLocationId]);

  const { data, isLoading, error } = useGraphData(config, {
    enabled: !!config && !!currentFY,
  });

  const contributors = useMemo(
    () => metersBehind(metric, meters, currentFY),
    [metric, meters, currentFY]
  );

  const stats = useMemo(() => {
    const rows = rowsOf(data);
    return metric.derived ? stpTreatedComparison(rows) : comparePeriods(rows);
  }, [data, metric.derived]);

  if (isLoading) {
    return (
      <div className="kpi-card ws-card">
        <h4 className="kpi-card-title">{metric.title}</h4>
        <div className="ws-skeleton" aria-label={`Loading ${metric.title}`} />
      </div>
    );
  }

  /* A request that failed and a meter that reported nothing are different
     facts, and saying "no reading arrived" for the first one blames the plant
     for what is actually a problem fetching the figure. */
  if (error) {
    return (
      <div className="kpi-card ws-card ws-card--dark">
        <h4 className="kpi-card-title">{metric.title}</h4>
        <p className="ws-empty">Could not load</p>
        <CardAbout description={metric.description} />
        <p className="ws-note ws-note--bad">
          The request for this figure failed, so nothing here is a reading.
        </p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="kpi-card ws-card ws-card--dark">
        <h4 className="kpi-card-title">{metric.title}</h4>
        <p className="ws-empty">Not reporting</p>
        <CardAbout description={metric.description} />
        <p className="ws-note ws-note--bad">
          {metric.derived
            ? 'Neither figure this is calculated from reported in this window.'
            : 'No reading arrived in this window.'}
        </p>
      </div>
    );
  }

  return (
    <div className={`kpi-card ws-card${metric.derived ? ' ws-card--derived' : ''}`}>
      <div className="ws-card-head">
        <h4 className="kpi-card-title">{metric.title}</h4>
        <ContributorsPopover contributors={contributors} title={metric.id} />
      </div>
      <p className="kpi-card-subtitle">{stats.latestLabel}</p>

      <div className="kpi-value">
        {stats.latestValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        <span className="kpi-unit">m³</span>
      </div>

      <div className={`kpi-change ${toneFor(stats.change)}`}>
        <FontAwesomeIcon icon={arrowFor(stats.change)} className="me-1" />
        <span>{Math.abs(stats.change).toFixed(1)}%</span>
        <span className="ws-delta">
          ({stats.change > 0 ? '+' : ''}{stats.changeValue.toFixed(2)} m³)
        </span>
      </div>

      <p className="ws-against">
        Vs {stats.previousLabel} &middot; {stats.previousValue.toFixed(2)} m³
      </p>

      <CardAbout description={metric.description} />

      {metric.derived && (
        <p className="ws-note">Calculated as: {metric.derived}</p>
      )}
    </div>
  );
};

export default WaterSummaryCard;
